/**
 * SchoolTransfers — campus, programme and study-mode moves with approval.
 * History is never touched: results, modules and fee rows stay with the
 * student; a transfer only changes where they are now.
 */
import React, { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import api, { getErrorMessage } from '@/lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  ArrowRight, ArrowLeftRight, Check, Loader2, Plus, Settings, X, UserRound,
} from 'lucide-react';

const STATUS_LABELS = {
  pending_receiving: 'Awaiting the receiving side',
  pending_registrar: 'Awaiting the registrar',
  approved: 'Approved',
  declined: 'Declined',
  cancelled: 'Cancelled',
};

const STATUS_STYLES = {
  pending_receiving: 'bg-amber-50 text-amber-700 border-amber-200',
  pending_registrar: 'bg-indigo-50 text-indigo-700 border-indigo-200',
  approved: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  declined: 'bg-red-50 text-red-700 border-red-200',
  cancelled: 'bg-slate-100 text-slate-500 border-slate-200',
};

const KIND_LABELS = { campus: 'Campus', programme: 'Programme', study_mode: 'Study mode' };

const readable = (v) => (v || '').replace(/_/g, ' ');

const SettingsCard = ({ tenantId, settings }) => {
  const queryClient = useQueryClient();
  const [form, setForm] = useState(settings);

  React.useEffect(() => setForm(settings), [settings]);

  const save = useMutation({
    mutationFn: async (body) => (await api.put(`/school/tenants/${tenantId}/transfer-settings`, body)).data,
    onSuccess: () => {
      toast.success('Transfer rules saved');
      queryClient.invalidateQueries({ queryKey: ['school-transfers', tenantId] });
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <Card data-testid="transfer-settings-card">
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <Settings className="h-4 w-4 text-slate-400" /> How transfers are handled
        </CardTitle>
        <CardDescription>Applies to every transfer raised from now on.</CardDescription>
      </CardHeader>
      <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="space-y-1.5">
          <Label className="text-xs">Approval</Label>
          <Select value={form.approval_mode}
                  onValueChange={(v) => setForm({ ...form, approval_mode: v })}>
            <SelectTrigger data-testid="transfer-approval-mode"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="single">Registrar decides</SelectItem>
              <SelectItem value="two_step">Receiving side accepts, then the registrar</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">Fees</Label>
          <Select value={form.fee_policy}
                  onValueChange={(v) => setForm({ ...form, fee_policy: v })}>
            <SelectTrigger data-testid="transfer-fee-policy"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="carry">The balance moves with the student</SelectItem>
              <SelectItem value="settle_first">Settle the balance before the move</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-3">
          <Label className="text-xs">Students may ask themselves</Label>
          <div className="flex items-center gap-3">
            <Switch checked={form.student_requests_enabled}
                    data-testid="transfer-student-requests"
                    onCheckedChange={(v) => setForm({ ...form, student_requests_enabled: v })} />
            <span className="text-sm text-slate-600">
              {form.student_requests_enabled ? 'On — from their portal' : 'Off — staff only'}
            </span>
          </div>
        </div>
        <div className="md:col-span-3">
          <Button size="sm" onClick={() => save.mutate(form)} disabled={save.isPending}
                  data-testid="transfer-settings-save">
            {save.isPending && <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />} Save rules
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

const NewTransferDialog = ({ open, onOpenChange, tenantId }) => {
  const queryClient = useQueryClient();
  const [studentId, setStudentId] = useState('');
  const [form, setForm] = useState({
    to_campus_id: '', to_programme_id: '', to_level_id: '', to_study_mode: '', reason: '',
  });

  const { data: studentsRaw } = useQuery({
    queryKey: ['school-students-for-transfer', tenantId],
    queryFn: async () => (await api.get(`/school/tenants/${tenantId}/students?limit=200`)).data,
    enabled: open,
  });
  const students = Array.isArray(studentsRaw) ? studentsRaw : (studentsRaw?.students || []);

  const { data: options } = useQuery({
    queryKey: ['school-transfer-options', tenantId, studentId],
    queryFn: async () => (await api.get(
      `/school/tenants/${tenantId}/students/${studentId}/transfer-options`)).data,
    enabled: open && !!studentId,
  });

  React.useEffect(() => {
    setForm({ to_campus_id: '', to_programme_id: '', to_level_id: '', to_study_mode: '', reason: '' });
  }, [studentId, open]);

  const levels = useMemo(() => {
    const programme = (options?.programmes || []).find(
      (p) => p.id === (form.to_programme_id || options?.student?.programme_id));
    return programme?.levels || [];
  }, [options, form.to_programme_id]);

  const create = useMutation({
    mutationFn: async () => (await api.post(`/school/tenants/${tenantId}/transfers`,
      { student_id: studentId, ...form })).data,
    onSuccess: () => {
      toast.success('Transfer request raised');
      queryClient.invalidateQueries({ queryKey: ['school-transfers', tenantId] });
      onOpenChange(false);
      setStudentId('');
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="transfer-dialog">
        <DialogHeader>
          <DialogTitle>Raise a transfer</DialogTitle>
          <DialogDescription>
            Only where the student is now changes — results, modules and fee history stay put.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 max-h-[65vh] overflow-y-auto pr-1">
          <div className="space-y-1.5">
            <Label className="text-xs">Student *</Label>
            <Select value={studentId} onValueChange={setStudentId}>
              <SelectTrigger data-testid="transfer-student-select">
                <SelectValue placeholder="Pick a student" />
              </SelectTrigger>
              <SelectContent>
                {students.map((s) => (
                  <SelectItem key={s.id} value={s.id}>
                    {s.first_name} {s.last_name}
                    {s.admission_number ? ` · ${s.admission_number}` : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {options && (
            <>
              <div className="rounded-md bg-slate-50 p-3 text-xs text-slate-600 space-y-1"
                   data-testid="transfer-current-state">
                <p><span className="font-semibold">Where they are now:</span>{' '}
                  {readable(options.student.study_mode) || 'mode not set'}</p>
                <p>Outstanding balance: <span className="font-semibold">
                  {Number(options.balance || 0).toLocaleString()}</span>
                  {options.settings.fee_policy === 'settle_first' && options.balance > 0 && (
                    <span className="text-red-600"> · must be settled before a move</span>
                  )}
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label className="text-xs">Move to campus</Label>
                  <Select value={form.to_campus_id}
                          onValueChange={(v) => setForm({ ...form, to_campus_id: v })}>
                    <SelectTrigger data-testid="transfer-campus-select">
                      <SelectValue placeholder="No change" />
                    </SelectTrigger>
                    <SelectContent>
                      {(options.campuses || []).map((c) => (
                        <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Move to programme</Label>
                  <Select value={form.to_programme_id}
                          onValueChange={(v) => setForm({ ...form, to_programme_id: v, to_level_id: '' })}>
                    <SelectTrigger data-testid="transfer-programme-select">
                      <SelectValue placeholder="No change" />
                    </SelectTrigger>
                    <SelectContent>
                      {(options.programmes || []).map((p) => (
                        <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {levels.length > 0 && (
                  <div className="space-y-1.5">
                    <Label className="text-xs">Level</Label>
                    <Select value={form.to_level_id}
                            onValueChange={(v) => setForm({ ...form, to_level_id: v })}>
                      <SelectTrigger data-testid="transfer-level-select">
                        <SelectValue placeholder="No change" />
                      </SelectTrigger>
                      <SelectContent>
                        {levels.map((l) => (
                          <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <div className="space-y-1.5">
                  <Label className="text-xs">Study mode</Label>
                  <Select value={form.to_study_mode}
                          onValueChange={(v) => setForm({ ...form, to_study_mode: v })}>
                    <SelectTrigger data-testid="transfer-mode-select">
                      <SelectValue placeholder="No change" />
                    </SelectTrigger>
                    <SelectContent>
                      {(options.study_modes || []).map((m) => (
                        <SelectItem key={m} value={m}>{readable(m)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs">Why</Label>
                <Textarea rows={3} value={form.reason} data-testid="transfer-reason-input"
                          onChange={(e) => setForm({ ...form, reason: e.target.value })}
                          placeholder="Moved city, changed calling, timetable clash…" />
              </div>
            </>
          )}
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={() => create.mutate()} data-testid="transfer-submit-btn"
                  disabled={!studentId || create.isPending}>
            {create.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Raise request
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const TransferRow = ({ row, tenantId }) => {
  const queryClient = useQueryClient();
  const [note, setNote] = useState('');
  const [open, setOpen] = useState(false);

  const act = useMutation({
    mutationFn: async ({ path, approve }) => (await api.post(
      `/school/tenants/${tenantId}/transfers/${row.id}/${path}`,
      path === 'cancel' ? {} : { approve, note })).data,
    onSuccess: (res) => {
      toast.success(res.status === 'approved' ? 'Transfer applied' : 'Decision recorded');
      queryClient.invalidateQueries({ queryKey: ['school-transfers', tenantId] });
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const pendingReceiving = row.status === 'pending_receiving';
  const pendingRegistrar = row.status === 'pending_registrar';

  return (
    <Card data-testid={`transfer-row-${row.id}`}>
      <CardContent className="py-4 space-y-3">
        <div className="flex flex-wrap items-start gap-4">
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <UserRound className="h-3.5 w-3.5 text-slate-400" />
              <span className="font-semibold text-slate-900">{row.student_name || 'Student'}</span>
              {row.student_number && (
                <span className="text-xs font-mono text-slate-400">{row.student_number}</span>
              )}
              <Badge variant="outline" className={`text-[10px] ${STATUS_STYLES[row.status] || ''}`}
                     data-testid={`transfer-status-${row.id}`}>
                {STATUS_LABELS[row.status] || row.status}
              </Badge>
              {row.raised_by_student && (
                <Badge variant="outline" className="text-[10px]">asked by the student</Badge>
              )}
            </div>
            <div className="mt-2 flex flex-wrap items-center gap-2 text-sm text-slate-600">
              {(row.kinds || []).map((k) => (
                <Badge key={k} variant="secondary" className="text-[10px]">{KIND_LABELS[k] || k}</Badge>
              ))}
              <span className="text-slate-400">
                {row.from.campus_name || '—'} · {row.from.programme_name || '—'} ·{' '}
                {readable(row.from.study_mode) || '—'}
              </span>
              <ArrowRight className="h-3.5 w-3.5 text-slate-400" />
              <span className="font-medium text-slate-800">
                {row.to.campus_name || '—'} · {row.to.programme_name || '—'} ·{' '}
                {readable(row.to.study_mode) || '—'}
              </span>
            </div>
            {row.reason && <p className="mt-2 text-xs text-slate-500">“{row.reason}”</p>}
            <p className="mt-2 text-xs text-slate-400">
              Balance when raised: {Number(row.balance_at_request || 0).toLocaleString()}
              {row.fee_policy === 'carry' ? ' · travels with the student' : ' · settled before the move'}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            {pendingReceiving && (
              <>
                <Button size="sm" data-testid={`transfer-accept-${row.id}`}
                        onClick={() => act.mutate({ path: 'receiving-decision', approve: true })}>
                  <Check className="h-3.5 w-3.5 mr-1.5" /> Receiving side accepts
                </Button>
                <Button size="sm" variant="outline" data-testid={`transfer-reject-${row.id}`}
                        onClick={() => act.mutate({ path: 'receiving-decision', approve: false })}>
                  <X className="h-3.5 w-3.5 mr-1.5" /> Decline
                </Button>
              </>
            )}
            {pendingRegistrar && (
              <>
                <Button size="sm" data-testid={`transfer-approve-${row.id}`}
                        onClick={() => act.mutate({ path: 'decision', approve: true })}>
                  <Check className="h-3.5 w-3.5 mr-1.5" /> Approve and move
                </Button>
                <Button size="sm" variant="outline" data-testid={`transfer-decline-${row.id}`}
                        onClick={() => act.mutate({ path: 'decision', approve: false })}>
                  <X className="h-3.5 w-3.5 mr-1.5" /> Decline
                </Button>
              </>
            )}
            {(pendingReceiving || pendingRegistrar) && (
              <Button size="sm" variant="ghost" data-testid={`transfer-cancel-${row.id}`}
                      onClick={() => act.mutate({ path: 'cancel' })}>Cancel</Button>
            )}
            <Button size="sm" variant="ghost" data-testid={`transfer-history-${row.id}`}
                    onClick={() => setOpen((v) => !v)}>
              {open ? 'Hide history' : 'History'}
            </Button>
          </div>
        </div>

        {(pendingReceiving || pendingRegistrar) && (
          <Input value={note} onChange={(e) => setNote(e.target.value)}
                 data-testid={`transfer-note-${row.id}`}
                 placeholder="Add a note to your decision (optional)" className="text-sm" />
        )}

        {open && (
          <div className="border-t border-slate-100 pt-3 space-y-1.5">
            {(row.history || []).map((h, i) => (
              <p key={i} className="text-xs text-slate-500">
                <span className="font-mono text-slate-400">
                  {new Date(h.at).toLocaleString()}
                </span>{' '}
                — {h.action} by {h.by || 'system'}{h.note ? ` · “${h.note}”` : ''}
              </p>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const SchoolTransfers = ({ tenantId }) => {
  const [filter, setFilter] = useState('open');
  const [dialog, setDialog] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['school-transfers', tenantId, filter],
    queryFn: async () => (await api.get(
      `/school/tenants/${tenantId}/transfers${filter === 'all' ? '' : `?status=${filter}`}`)).data,
    enabled: !!tenantId,
  });

  if (isLoading) return <Skeleton className="h-64 w-full" />;

  const rows = data?.transfers || [];
  const counts = data?.counts || {};

  return (
    <div className="space-y-6" data-testid="school-transfers">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2">
            <ArrowLeftRight className="h-5 w-5 text-indigo-500" /> Transfers
          </h2>
          <p className="text-sm text-slate-500 mt-1">
            Campus, programme and study-mode moves. Nothing a student has already done is lost.
          </p>
        </div>
        <Button onClick={() => setDialog(true)} data-testid="transfer-new-btn">
          <Plus className="h-4 w-4 mr-2" /> Raise a transfer
        </Button>
      </div>

      <SettingsCard tenantId={tenantId} settings={data?.settings
        || { approval_mode: 'two_step', fee_policy: 'carry', student_requests_enabled: true }} />

      <div className="flex flex-wrap gap-2">
        {[['open', `Awaiting a decision (${(counts.pending_receiving || 0) + (counts.pending_registrar || 0)})`],
          ['approved', `Approved (${counts.approved || 0})`],
          ['declined', `Declined (${counts.declined || 0})`],
          ['all', 'Everything']].map(([key, label]) => (
            <button key={key} onClick={() => setFilter(key)}
                    data-testid={`transfer-filter-${key}`}
                    className={`px-3 py-1.5 text-xs font-medium rounded-full border transition-colors
                      ${filter === key ? 'bg-slate-900 text-white border-slate-900'
                                       : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'}`}>
              {label}
            </button>
          ))}
      </div>

      {rows.length === 0 ? (
        <Card><CardContent className="py-14 text-center" data-testid="transfers-empty">
          <ArrowLeftRight className="h-8 w-8 mx-auto text-slate-300" />
          <p className="mt-4 text-sm text-slate-500">Nothing here yet.</p>
        </CardContent></Card>
      ) : (
        <div className="space-y-3">
          {rows.map((row) => <TransferRow key={row.id} row={row} tenantId={tenantId} />)}
        </div>
      )}

      <NewTransferDialog open={dialog} onOpenChange={setDialog} tenantId={tenantId} />
    </div>
  );
};

export default SchoolTransfers;
